<template>
  <DataTable
    class="bom-table"
    :value="rows"
    data-key="id"
    scrollable
    show-gridlines
    removable-sort
    row-group-mode="subheader"
    group-rows-by="statusPriority"
    sort-field="statusPriority"
    :sort-order="1"
    expandable-row-groups
    v-model:expanded-row-groups="expandedGroups"
    :pt="{
      rowGroupHeaderCell: { colspan: COLUMN_COUNT },
      // The group title toggles instead
      rowToggleButton: { class: 'd-none' },
    }"
  >
    <template #header>
      <div class="d-flex flex-wrap align-items-center gap-2">
        <IconField class="flex-grow-1">
          <InputIcon>
            <i class="fa fa-search" />
          </InputIcon>
          <InputText
            v-model="searchTerm"
            :placeholder="$t('BomPartTable.keywordSearch')"
            class="w-100"
          />
        </IconField>

        <div class="btn-group" role="group">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :class="{ active: grouped }"
            :title="$t('BomPartTable.groupedHint')"
            @click="grouped = true"
          >
            <i class="fas fa-network-wired me-1" />
            {{ $t('BomPartTable.grouped') }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            :class="{ active: !grouped }"
            :title="$t('BomPartTable.individualHint')"
            @click="grouped = false"
          >
            <i class="fas fa-list me-1" />
            {{ $t('BomPartTable.individual') }}
          </button>
        </div>
      </div>

      <div v-if="bomStore.focusedStatus" class="mt-2">
        <span class="d-inline-flex align-items-center gap-2 p-0">
          <i class="fas fa-filter" />
          {{ $t('BomPartTable.showingOnly', {
            group: $t(`BomPartTable.header_${bomStore.focusedStatus.toLowerCase()}`)
          }) }}
          <button
            type="button"
            class="btn btn-link p-0"
            @click="bomStore.clearFocus()"
          >
            {{ $t('BomPartTable.showAll') }}
          </button>
        </span>
      </div>
    </template>

    <Column
      field="name"
      :header="$t('BomPartTable.name')"
      sortable
      :sort-field="(entry) => sortKey(entry.name)"
      style="width: 15%"
    >
      <template #body="{ data }">
        {{ data.name }}
        <span
          v-if="data.mismatchedFootprint"
          data-bs-toggle="popover"
          data-bs-trigger="hover focus"
          data-bs-placement="right"
          :data-bs-content="$t('BomPartTable.mismatchedFootprintWarning', {
            normalizedFootprint: data.normalizedFootprint,
            assignedFootprint: data.assignedFootprint
          })"
          data-bs-html="true"
        >
          <span class="fas fa-exclamation-triangle text-warning" />
        </span>
      </template>
    </Column>

    <Column :header="$t('BomPartTable.part')" body-class="break-words" style="width: 45%">
      <template #body="{ data }">
        <div class="row gx-2 align-items-center">
          <!-- Part description -->
          <div class="col">
            <div v-if="data.mpn">
              {{ data.mpn }}

              <div class="d-inline-flex gap-1">
                <a
                  type="button"
                  class="text-secondary icon-action"
                  @click.stop="copyMpnToClipboard($event, data.mpn)"
                  v-bs-tooltip="{ title: $t('BomPartTable.copyMpnTooltip'), placement: 'top' }"
                >
                  <i class="fa-regular fa-copy" />
                </a>

                <a
                  v-if="data.datasheet"
                  :href="data.datasheet"
                  target="_blank"
                  class="text-secondary icon-action"
                  @click.stop
                  v-bs-tooltip="{ title: $t('BomPartTable.datasheet'), placement: 'top' }"
                >
                  <i class="fa-regular fa-file-pdf" />
                </a>
              </div>
            </div>

            <div
              class="text-secondary"
              v-if="data.manufacturer"
            >
              {{ data.manufacturer }}
            </div>

            <div
              v-if="isUnavailableSmartMatch(data)"
              data-bs-toggle="popover"
              data-bs-trigger="manual"
              data-bs-placement="top"
              data-bs-html="true"
              :data-bs-content="$t('BomPartTable.smartMatchNotResolved')"
            >
              <i class="fas fa-exclamation-triangle text-warning me-1" />
              <abbr class="text-warning-emphasis">{{ $t('BomPartTable.smartMatchNotResolvedAction') }}</abbr>
            </div>

            <span v-if="!data.mpn">
              {{ $t('BomPartTable.unassigned') }}
            </span>
          </div>

          <!-- Part actions -->
          <div class="col-auto d-flex flex-column flex-xxl-row gap-1">
            <button
              v-if="data.status === 'AUTO_ASSIGNED'"
              type="button"
              class="btn btn-sm btn-outline-success text-nowrap"
              :disabled="bomStore.loading"
              @click="bomStore.confirmParts(data.parts)"
            >
              <i class="fas fa-check me-1" />
              {{ $t('BomPartTable.confirm') }}
            </button>

            <button
              v-else
              type="button"
              class="btn btn-sm btn-outline-secondary text-nowrap"
              :disabled="bomStore.loading"
              @click="assignStore.startAssignment(data.parts)"
            >
              <template v-if="data.mpn">
                <i class="fas fa-pen me-1" />
                {{ $t('BomPartTable.change') }}
              </template>
              <template v-else>
                <i class="fas fa-magnifying-glass me-1" />
                {{ $t('BomPartTable.assign') }}
              </template>
            </button>

            <!-- Unconfirmed matches are either accepted or dropped, like on master -->
            <button
              v-if="data.status === 'AUTO_ASSIGNED'"
              type="button"
              class="btn btn-sm btn-outline-secondary text-nowrap"
              :disabled="bomStore.loading"
              :title="$t('BomPartTable.unassignPartHint')"
              @click="bomStore.unassignParts(data.parts)"
            >
              <i class="fas fa-rotate-left me-1" />
              {{ $t('BomPartTable.unassign') }}
            </button>

            <!-- Without a part there is nothing else to choose than leaving it out -->
            <button
              v-else-if="data.status === 'NOT_ASSIGNED'"
              type="button"
              class="btn btn-sm btn-outline-secondary text-nowrap"
              :disabled="bomStore.loading"
              @click="bomStore.excludeParts(data.parts, true)"
            >
              <i class="fas fa-ban me-1" />
              {{ $t('BomPartTable.exclude') }}
            </button>

            <div v-else class="dropdown">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary dropdown-toggle text-nowrap"
                data-bs-toggle="dropdown"
                :data-bs-popper-config="JSON.stringify({ strategy: 'fixed' })"
                aria-expanded="false"
              >
                <i class="fas fa-sliders me-1" />
                {{ $t('BomPartTable.options') }}
              </button>

              <div class="dropdown-menu dropdown-menu-end row-menu">
                <button
                  type="button"
                  class="dropdown-item d-flex align-items-center gap-2"
                  :class="{ active: data.status === 'EXCLUDED' }"
                  :disabled="bomStore.loading || data.status === 'EXCLUDED'"
                  :title="$t('BomPartTable.sourcing_excluded_hint')"
                  @click="bomStore.excludeParts(data.parts, true)"
                >
                  <i class="fas fa-ban row-menu-icon" />
                  {{ $t('BomPartTable.sourcing_excluded') }}
                </button>

                <button
                  v-if="data.mpn"
                  type="button"
                  class="dropdown-item d-flex align-items-center gap-2"
                  :disabled="bomStore.loading"
                  :title="$t('BomPartTable.unassignPartHint')"
                  @click="bomStore.unassignParts(data.parts)"
                >
                  <i class="fas fa-rotate-left row-menu-icon" />
                  {{ $t('BomPartTable.unassignPart') }}
                </button>

                <div class="dropdown-divider" />
                <h6 class="dropdown-header">
                  {{ $t('BomPartTable.sourcingHeader') }}
                </h6>
                <button
                  v-for="option in sourcingOptions(data)"
                  :key="option"
                  type="button"
                  class="dropdown-item d-flex align-items-center gap-2"
                  :class="{ active: data.sourcing === option }"
                  :disabled="bomStore.loading || data.sourcing === option"
                  :title="$t(`BomPartTable.sourcing_${option.toLowerCase()}_hint`)"
                  @click="bomStore.setSourcing(data.parts, option)"
                >
                  <i
                    class="fas fa-check row-menu-icon"
                    :class="{ invisible: data.sourcing !== option }"
                  />
                  {{ $t(`BomPartTable.sourcing_${option.toLowerCase()}`) }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Column>

    <Column :header="$t('BomPartTable.comment')" body-class="break-words">
      <template #body="{ data }">
        {{ bomStore.mpnPropertyKey ? (data.edaMpn || '-') : data.comment }}
      </template>
    </Column>

    <Column :header="$t('BomPartTable.footprint')" body-class="break-words" style="width: 12%">
      <template #body="{ data }">
        {{ data.normalizedFootprint || data.footprint }}
        <i
          v-if="data.normalizedFootprint !== null"
          class="fas fa-info-circle text-secondary"
          data-bs-toggle="popover"
          data-bs-trigger="hover focus"
          data-bs-placement="right"
          data-bs-html="true"
          :data-bs-content="$t('BomPartTable.footprintNotice', { footprint: data.footprint })"
        />
      </template>
    </Column>

    <Column
      :header="$t('BomPartTable.price')"
      sortable
      :sort-field="(entry) => entry.priceCents || 0"
    >
      <template #body="{ data }">
        <div
          v-if="data.availability !== 'unavailable'
            && data.priceCents !== null
            && data.status !== 'EXCLUDED'"
        >
          <span
            data-bs-toggle="popover"
            data-bs-trigger="hover focus"
            data-bs-placement="bottom"
            :data-bs-content="$t(`BomPartTable.priceImpactNotice${priceImpact(data.priceCents)}`)"
            data-bs-html="true"
          >
            <i
              v-for="step in 4"
              :key="step"
              class="fas fa-euro-sign"
              :class="{ 'active-price': priceImpact(data.priceCents) >= step }"
            />
          </span>
        </div>
      </template>
    </Column>

    <Column
      field="availability"
      :header="$t('BomPartTable.availability')"
      sortable
      :sort-field="(entry) => availabilitySortPriority(entry.availability)"
      body-class="text-center"
    >
      <template #body="{ data }">
        <span
          v-if="isUnavailableSmartMatch(data)"
          data-bs-toggle="popover"
          data-bs-trigger="manual"
          data-bs-placement="left"
          data-bs-html="true"
          :data-bs-content="$t('BomPartTable.smartMatchNotResolved')"
        >
          <StockIndicator :availability="data.availability" />
        </span>
        <StockIndicator v-else :availability="data.availability" />
      </template>
    </Column>

    <template #groupheader="{ data }">
      <div class="sub-header d-flex align-items-center justify-content-between gap-2">
        <button
          type="button"
          class="btn btn-link text-reset text-decoration-none text-start p-0 d-flex align-items-center gap-2"
          :aria-expanded="expandedGroups.includes(data.statusPriority)"
          @click="toggleGroup(data.statusPriority)"
        >
          <i
            class="fas fa-chevron-down sub-header-chevron"
            :class="{ 'is-collapsed': !expandedGroups.includes(data.statusPriority) }"
          />
          <span>
            <span class="sub-header-title">
              {{ $t(`BomPartTable.header_${data.status.toLowerCase()}`) }}
              ({{ $t('BomPartTable.header_components', groupParts(data.status).length) }})
            </span>
            <span class="d-block fw-normal text-body-secondary">
              {{ $t(`BomPartTable.groupHint_${data.status.toLowerCase()}`) }}
            </span>
          </span>
        </button>

        <button
          v-if="data.status === 'AUTO_ASSIGNED'"
          type="button"
          class="btn btn-sm btn-outline-success flex-shrink-0"
          :disabled="bomStore.loading"
          @click="bomStore.confirmParts(groupParts(data.status))"
        >
          <i class="fas fa-check me-1" />
          {{ $t('BomPartTable.confirmAll') }}
        </button>

        <button
          v-else-if="data.status === 'NOT_ASSIGNED'"
          type="button"
          class="btn btn-sm btn-outline-secondary flex-shrink-0"
          :disabled="bomStore.loading"
          @click="startAssignmentWizard"
        >
          <i class="fas fa-magnifying-glass me-1" />
          {{ $t('BomPartTable.assignAll') }}
        </button>
      </div>
    </template>

    <template #empty>
      <div class="p-2 d-flex flex-column justify-content-center align-items-center">
        <template v-if="bomStore.loading || bomStore.areJobsRunning">
          <span class="spinner-border spinner-border-sm mb-2" role="status" />
          {{ $t('BomPartTable.loading') }}
        </template>

        <template v-else-if="bomStore.parts.length > 0">
          <i class="fas fa-magnifying-glass fa-lg mb-2" />
          <p class="mb-0">
            {{ $t('BomPartTable.noPartsFound') }}
          </p>
        </template>

        <template v-else>
          <i class="fas fa-microchip fa-lg mb-2" />
          <p class="mb-1">
            <strong>{{ $t('BomPartTable.noPartsTitle') }}</strong>
          </p>
          <p v-html="$t('BomPartTable.noPartsDetails')" class="width-75 text-center mb-0" />
        </template>
      </div>
    </template>
  </DataTable>
</template>

<script>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';

import { useBomStore, useAssignStore } from 'store/bom';
import messages from 'locales/BomPartTable';

import _ from 'lodash';
import Part from 'store/bom/Part';
import { FilterService } from '@primevue/core/api';
import { Tooltip } from 'bootstrap';
import StockIndicator from './StockIndicator.vue';
import TableEntry, { groupSimilarParts } from './lib/TableEntry';

const COLUMN_COUNT = 6;

const SEARCH_FIELDS = ['name', 'mpn', 'manufacturer', 'comment', 'edaMpn', 'footprint', 'normalizedFootprint'];

export default {
  name: 'BomPartTable',
  components: {
    StockIndicator,
    DataTable,
    Column,
    InputText,
    IconField,
    InputIcon,
  },
  setup() {
    const bomStore = useBomStore();
    const assignStore = useAssignStore();
    return { bomStore, assignStore, COLUMN_COUNT };
  },
  data() {
    return {
      grouped: true,
      searchTerm: '',
      expandedGroups: Object.values(Part.STATUS_PRIORITY),
    };
  },
  computed: {
    visibleParts() {
      if (this.bomStore.areForegroundJobsRunning) return [];

      const { parts, focusedStatus } = this.bomStore;
      return focusedStatus ? parts.filter((part) => part.getStatus() === focusedStatus) : parts;
    },
    entries() {
      const groups = this.grouped
        ? groupSimilarParts(this.visibleParts)
        : this.visibleParts.map((part) => [part]);
      return groups.map((parts) => TableEntry.createFromParts(parts));
    },

    rows() {
      const matching = this.entries.filter((entry) => SEARCH_FIELDS
        .some((field) => FilterService.filters.contains(entry[field], this.searchTerm)));
      return _.sortBy(matching, ['statusPriority', (entry) => this.sortKey(entry.name)]);
    },
    // Always the whole BOM, even while the table is narrowed
    bomPrice() {
      return _.sumBy(this.bomStore.parts, (part) => part.priceCents || 0);
    },
  },
  methods: {
    sortKey(value) {
      return String(value ?? '').toLowerCase().replace(/\d+/g, (digits) => digits.padStart(10, '0'));
    },
    // Worst availability first
    availabilitySortPriority(availability) {
      const priority = { unavailable: 0, risky: 1, available: 2 };
      return priority[availability] ?? 3;
    },
    toggleGroup(statusPriority) {
      this.expandedGroups = _.xor(this.expandedGroups, [statusPriority]);
    },
    groupParts(status) {
      return this.rows.filter((entry) => entry.status === status).flatMap((entry) => entry.parts);
    },

    sourcingOptions(row) {
      const { AISLER, CUSTOMER } = Part.SOURCING;
      return row.physicalPart ? [AISLER, CUSTOMER] : [AISLER];
    },
    startAssignmentWizard() {
      const unassigned = this.entries.filter((entry) => entry.status === Part.NOT_ASSIGNED);
      this.assignStore.startWizard(unassigned.map((entry) => entry.parts));
    },
    priceImpact(cents) {
      if (!cents) return 0;

      const share = cents / this.bomPrice;
      if (share >= 0.3) return 4;
      if (share >= 0.15) return 3;
      if (share > 0.05) return 2;
      return 1;
    },
    isUnavailableSmartMatch(row) {
      return row.parts[0]?.assignment?.type === 'smart_match' && row.availability === 'unavailable';
    },
    async copyMpnToClipboard(event, mpn) {
      const tooltip = Tooltip.getInstance(event.currentTarget);
      await navigator.clipboard.writeText(mpn);
      tooltip.setContent({ '.tooltip-inner': this.$t('BomPartTable.copied') });
      setTimeout(() => {
        tooltip.setContent({ '.tooltip-inner': this.$t('BomPartTable.copyMpnTooltip') });
      }, 1000);
    },
  },
  i18n: { messages },
};
</script>

<style scoped lang="scss">
.bom-table {
  .sub-header-title {
    font-weight: var(--bs-font-weight-semibold, 450);
  }

  .sub-header-chevron {
    transition: transform 0.15s;

    &.is-collapsed {
      transform: rotate(-90deg);
    }
  }

  abbr {
    text-decoration: underline dotted;
  }
}

.row-menu {
  --bs-dropdown-min-width: 13rem;
}

.row-menu-icon {
  width: 0.85rem;
  flex-shrink: 0;
  font-size: 0.75rem;
}

.fa-euro-sign {
  opacity: 0.2;
  margin-right: -0.15rem;
}

.active-price {
  opacity: 1;
}

.break-words {
  word-break: break-all;
}

.dropdown-header {
  color: var(--bs-body-color);
}
</style>
